
import { useEffect, useState } from "react";

import { createUrlSearchParams, GridMetaData } from "models/grids";
import { IRelatedWorkItem } from "models/work-items";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import IRelatedWorkItemController from "./IRelatedWorkItemController";

type UseRelatedWorkItemsSignature = (
    workItemId: number, 
    gridMetadata: GridMetaData,
    refresh: () => void,
    refreshIndex: any,
    draft?: boolean
) => IRelatedWorkItemController;

type GetRelatedWorkItemsResult = {
    data: IRelatedWorkItem[];
    count: number;
};

const useRelatedWorkItems: UseRelatedWorkItemsSignature = (workItemId, gridMetadata, refresh, refreshIndex, draft) => {
    const [ values, setValues ] = useState<IRelatedWorkItem[]>([]);
    const [ count, setCount ] = useState<number>(0);
    const [ loading, setLoading ] = useState<boolean>(false);
    const [ lastQuery, setLastQuery ] = useState<string>('');
    const [ lastRefresh, setLastRefresh ] = useState(refreshIndex);

    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    useEffect(() => {
        const searchParams = createUrlSearchParams(gridMetadata);
        const query = `?${searchParams}`;
        if (lastRefresh === refreshIndex && lastQuery === query) 
            return;
        if (!user || !user.access_token) 
            return;

        const controller = new AbortController();
        (async () => {
            setLoading(true);
            try {
                let url = `/api/v3/workitems/${workItemId}/related${query}`;
                if (draft) {
                    url += `&draft=${true}`;
                }
                const resp = await fetcher(url, {
                    signal: controller.signal
                }, async resp => {
                    return true;
                });

                if (!resp.ok) {
                    return;
                }

                const result = await resp.json() as GetRelatedWorkItemsResult;
                setValues(result.data);

                if (result.count !== count)
                    setCount(result.count);
                
                setLastQuery(query);
                setLastRefresh(refreshIndex);
                
            } catch (err) {

            } finally {
                if (!controller.signal.aborted) 
                    setLoading(false);
            }
        })();

        return () => controller.abort();
    }, [ user, workItemId, gridMetadata, refreshIndex ]);

    const controller: IRelatedWorkItemController = {
        values,
        count,
        loading,
        refresh
    };

    return controller;
};

export default useRelatedWorkItems;